import { Box, Divider, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import useSystemVars from 'hooks/useSystemVars';
import React from 'react';
import { ChatUserModel } from 'types/chat-users/chat-user';
import { SystemVariable } from 'types/system/system-vars';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface SystemVarsMenuProps {
  anchorEl: HTMLElement | null | undefined;
  open: boolean;
  handleMenuClose: () => void;
  chatUser?: ChatUserModel | null;
  onItemClick: (variable: SystemVariable) => void;
  extraVariables?: SystemVariable[];
  extraVariablesSectionLabel?: string;
}

const SystemVarsMenu: React.FC<SystemVarsMenuProps> = ({
  anchorEl,
  open,
  handleMenuClose,
  chatUser,
  onItemClick,
  extraVariables,
  extraVariablesSectionLabel,
}) => {
  const { systemVars, systemVarsWithChildren } = useSystemVars();

  const [showChildren, setShowChildren] = React.useState<string | null>(null);

  const combinedVars = chatUser ? [...systemVars, ...systemVarsWithChildren] : systemVars;

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
      {showChildren ? (
        <React.Fragment>
          <MenuItem>
            <Stack direction="row" spacing={2} alignItems={'center'} onClick={() => setShowChildren(null)}>
              Go back
              <ArrowBackIcon
                sx={{
                  fontSize: 20,
                }}
              />
            </Stack>
          </MenuItem>
          {combinedVars
            .find((param) => param.varName === showChildren)
            ?.children?.(chatUser)
            ?.map((param) => (
              <Tooltip title={param.label || 'ללא תגית'} placement="right" key={param.varName}>
                <MenuItem
                  onClick={() => {
                    onItemClick(param);
                    setShowChildren(null);
                  }}
                >
                  {chatUser ? param.valueGetter(chatUser) : param.label}
                </MenuItem>
              </Tooltip>
            ))}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {combinedVars.map((param) => {
            const value = chatUser && param.valueGetter(chatUser);
            return (
              <Tooltip title={param.label} placement="right" key={param.varName}>
                <Box width="100%">
                  <MenuItem
                    onClick={() => (param.children ? setShowChildren(param.varName) : onItemClick(param))}
                    disabled={Boolean(chatUser) && !value}
                  >
                    {chatUser ? value || 'Not available' : param.label}
                  </MenuItem>
                </Box>
              </Tooltip>
            );
          })}
          {extraVariables && extraVariables.length > 0 && (
            <React.Fragment>
              <Divider sx={{ my: 0.5 }} />
              {extraVariablesSectionLabel ? (
                <Box px={2} py={0.75}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {extraVariablesSectionLabel}
                  </Typography>
                </Box>
              ) : null}
              {extraVariables.map((param) => (
                <Tooltip title={param.label} placement="right" key={`extra-${param.varName}`}>
                  <Box width="100%">
                    <MenuItem onClick={() => onItemClick(param)}>{param.label}</MenuItem>
                  </Box>
                </Tooltip>
              ))}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Menu>
  );
};

export default React.memo(SystemVarsMenu);
